<template>
  <div
    class="field"
    data-test="select-unit"
  >
    <AutoComplete
      ref="autocomplete"
      v-model="unit_name"
      :suggestions="filtered_units"
      :disabled="disabled"
      optionLabel="attributes.name_with_customer"
      field="attributes.name_with_customer"
      :showClear="true"
      completeOnFocus
      inputClass="w-full"
      class="h-9 w-full"
      :pt="{
        pcInputText: {
          root: { 'data-test': 'select-unit-input' },
        },
      }"
      @complete="on_auto_search"
      @item-select="select_unit"
      @option-select="select_unit"
      @clear="clear_unit"
      @update:modelValue="on_unit_name_update"
    >
      <template #option="slotProps">
        <span class="flex shrink flex-row items-center gap-1">
          <template v-if="!project_id">
            <span class="min-w-12.5 truncate">{{ slotProps.option.attributes.project_name }}</span>
            <span>-</span>
          </template>
          <span class="truncate">{{ slotProps.option.attributes.name_with_customer }}</span>
        </span>
      </template>

      <template #empty>
        <div data-test="empty-result">
          <div v-if="is_searching">
            {{ $t('select.actions.noResults') }}
            <span v-if="unit_name && typeof unit_name === 'string'">"{{ unit_name }}"</span>
          </div>
          <div v-else>
            {{ $t('select.actions.startTyping') }}
          </div>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useToasts } from '@/app/util/toasts';
import _ from 'lodash-es';
import AutoComplete from 'primevue/autocomplete';

const props = defineProps({
  active: { type: Object, default: null },
  modelValue: { type: Object, default: null },
  project_id: { type: String, default: null },
  units_prop: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['changeSelect']);

const store = useStore();
const toast_action_failed = useToasts().toast_action_failed;

const unit_name = ref('');
const is_searching = ref(false);
const units_search = ref([]);

const selected_unit = computed(() => props.modelValue || props.active);

watch(
  selected_unit,
  (new_val) => {
    unit_name.value = _.get(new_val, 'attributes.name', '');
  },
  { immediate: true },
);

const filtered_units = computed(() => {
  const units = props.units_prop || units_search.value || [];
  const query = typeof unit_name.value === 'string' ? unit_name.value.toLowerCase() : '';

  return _.filter(units, (p) => {
    const label = _.get(p, 'attributes.name_with_customer', '');
    return label.toLowerCase().indexOf(query) >= 0;
  });
});

const autocomplete_units = _.debounce(async (search = '') => {
  try {
    const results = await store.dispatch(
      'AUTOCOMPLETE_UNITS',
      _.pickBy({
        name_or_customer_group_name_cont: search,
        project_id: props.project_id,
        customer_group_id_not_null: true,
      }),
    );
    units_search.value = results;
  } catch (error) {
    toast_action_failed({ error: error });
  }
}, 250);

const search_unit = (query) => {
  if (!query) {
    is_searching.value = false;
    autocomplete_units('');
    return;
  }

  if (query.length >= 1) {
    is_searching.value = true;
    autocomplete_units(query);
  } else {
    is_searching.value = false;
  }
};

const on_auto_search = (event) => {
  search_unit(event?.query || '');
};

const select_unit = (event) => {
  const selected = event && event.value ? event.value : event;
  emit('changeSelect', selected);
};

const clear_unit = () => {
  select_unit(null);
  unit_name.value = '';
  is_searching.value = false;
};

const on_unit_name_update = (value) => {
  if (value && typeof value === 'object') {
    select_unit(value);
  }
};
</script>
